// Reservasi / booking meja (halaman Reservasi di web admin & kasir): label, aturan aksi yang tersedia, dan pesan WhatsApp ke pelanggan.
// Aturan status & DP yang sebenarnya ada di backend (utils/reservasi.js); di sini hanya menentukan tombol apa yang ditampilkan.

export const STATUS_AKTIF = ['dikonfirmasi', 'datang'];

/** Semua id meja yang dipegang reservasi: meja utama lalu meja tambahan (rombongan besar). */
export const semuaMejaReservasi = (r) => {
  const ids = [];
  if (r?.tableId) ids.push(Number(r.tableId));
  const tambahan = Array.isArray(r?.mejaTambahanId) ? r.mejaTambahanId : (r?.mejaTambahan || []).map((m) => m.id);
  tambahan.forEach((id) => { if (id && !ids.includes(Number(id))) ids.push(Number(id)); });
  return ids;
};

/** "T1 + T2" (meja utama lalu tambahan); '' bila reservasi tanpa meja. */
export const namaMejaReservasi = (r) => [r?.meja?.name, ...(r?.mejaTambahan || []).map((m) => m.name)].filter(Boolean).join(' + ');

export const STATUS_LABEL = {
  dikonfirmasi: 'Dikonfirmasi',
  datang: 'Sudah datang',
  selesai: 'Selesai',
  batal: 'Dibatalkan',
  tidak_datang: 'Tidak datang',
};

export const STATUS_WARNA = {
  dikonfirmasi: 'primary',
  datang: 'success',
  selesai: 'default',
  batal: 'error',
  tidak_datang: 'warning',
};

export const DP_LABEL = {
  tidak_ada: 'Tanpa DP',
  menunggu_bayar: 'DP menunggu pembayaran QRIS',
  diterima: 'DP diterima',
  dipakai: 'DP dipakai',
  dikembalikan: 'DP dikembalikan',
  hangus: 'DP hangus',
};

export const DP_WARNA = {
  tidak_ada: 'default',
  menunggu_bayar: 'warning',
  diterima: 'success',
  dipakai: 'default',
  dikembalikan: 'info',
  hangus: 'error',
};

export const METODE_DP_LABEL = { cash: 'Tunai', noncash: 'Non-tunai', qris: 'QRIS' };

export const rupiah = (n) => `Rp ${Math.round(Number(n) || 0).toLocaleString('id-ID')}`;

/** Tanggal "YYYY-MM-DD" digeser n hari (tanpa zona waktu, aman untuk pergantian bulan/tahun). */
export const geserTanggal = (tanggal, n) => {
  const [y, m, d] = String(tanggal).split('-').map(Number);
  const x = new Date(Date.UTC(y, m - 1, d + n));
  return x.toISOString().slice(0, 10);
};

const HARI = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
const BULAN = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

/** "Sabtu, 24 Oktober 2026" dari "YYYY-MM-DD". */
export const tanggalPanjang = (tanggal) => {
  const [y, m, d] = String(tanggal).split('-').map(Number);
  if (!y || !m || !d) return '';
  const hari = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${HARI[hari]}, ${d} ${BULAN[m - 1]} ${y}`;
};

/**
 * Tombol yang tersedia untuk satu reservasi (bentuk dari API backend):
 *  datang, ubah, batal, tidakDatang, selesai, tambahDp (belum ada DP), lanjutQris (QRIS menunggu bayar), keputusanDp (DP diterima tapi reservasi ditutup).
 */
export const aksiTersedia = (r) => {
  const aktif = STATUS_AKTIF.includes(r.status);
  const dp = r.dp?.status || 'tidak_ada';
  return {
    datang: r.status === 'dikonfirmasi',
    ubah: aktif,
    batal: aktif,
    tidakDatang: r.status === 'dikonfirmasi',
    selesai: r.status === 'datang',
    tambahDp: aktif && (dp === 'tidak_ada' || dp === 'menunggu_bayar'),
    lanjutQris: aktif && dp === 'menunggu_bayar' && r.dp?.metode === 'qris',
    keputusanDp: !aktif && dp === 'diterima',
    // DP yang bisa dipotong saat pembayaran di kasir
    dpBisaDipakai: aktif && dp === 'diterima',
  };
};

/** Nomor telepon (angka) untuk tautan wa.me; null bila tidak ada. */
export const nomorWa = (telepon) => {
  const t = String(telepon || '').replace(/\D/g, '');
  return t.length >= 9 ? t : null;
};

/** Teks WhatsApp ke pelanggan sesuai status reservasi. */
export const teksWa = (r) => {
  const waktu = `${tanggalPanjang(r.tanggal)} pukul ${String(r.jam).replace(':', '.')}`;
  const tempat = r.namaCabang ? ` di ${r.namaCabang}` : '';
  const meja = r.meja?.name ? `, meja ${namaMejaReservasi(r)}` : '';
  const kode = `Kode booking: ${r.kode}.`;
  const tautan = r.tautanStatus ? ` Cek status reservasi: ${r.tautanStatus}` : '';
  if (r.status === 'batal') return `Halo ${r.nama}, reservasi${tempat} (${kode.replace('.', '')}) pada ${waktu} sudah dibatalkan. Terima kasih.`;
  if (r.status === 'tidak_datang') return `Halo ${r.nama}, reservasi${tempat} (${kode.replace('.', '')}) pada ${waktu} kami tandai tidak datang. Hubungi kami bila ingin menjadwalkan ulang.`;
  const dp = r.dp?.status === 'diterima' ? ` DP ${rupiah(r.dp.nominal)} sudah kami terima.` : '';
  return `Halo ${r.nama}, reservasi${tempat} untuk ${r.jumlahOrang} orang pada ${waktu}${meja} sudah dikonfirmasi. ${kode}${dp}${tautan} Sampai jumpa!`;
};

/** Tautan wa.me dengan pesan siap kirim; null bila nomor tidak ada. */
export const tautanWa = (r) => {
  const nomor = nomorWa(r.telepon);
  return nomor ? `https://wa.me/${nomor}?text=${encodeURIComponent(teksWa(r))}` : null;
};

/** Tombol keputusan DP yang perlu ditanyakan saat menutup reservasi (batal / tidak datang / selesai): true bila DP sudah diterima. */
export const perluKeputusanDp = (r) => r?.dp?.status === 'diterima';

/** Ringkasan DP satu baris untuk kartu: "DP diterima Rp 50.000 (Tunai)". */
export const ringkasDp = (r) => {
  const dp = r.dp || {};
  if (!dp.status || dp.status === 'tidak_ada') return DP_LABEL.tidak_ada;
  const metode = dp.metode === 'noncash' ? (dp.nonCashMethodName || 'Non-tunai') : METODE_DP_LABEL[dp.metode] || '';
  return `${DP_LABEL[dp.status]} ${rupiah(dp.nominal)}${metode ? ` (${metode})` : ''}`;
};

/** Tanggal & jam sekarang (YYYY-MM-DD, HH:mm) menurut zona IANA (zona cabang). */
export const sekarangDiZona = (iana = 'Asia/Jakarta', sekarang = new Date()) => {
  const bagian = new Intl.DateTimeFormat('en-CA', { timeZone: iana, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(sekarang).reduce((o, p) => ({ ...o, [p.type]: p.value }), {});
  return { tanggal: `${bagian.year}-${bagian.month}-${bagian.day}`, jam: `${bagian.hour}:${bagian.minute}` };
};
